import React, { useState, useEffect } from 'react';
import { Page, Text, Document, StyleSheet, View, Image } from '@react-pdf/renderer';
import { FormattedMessage } from 'react-intl';
import get from 'lodash/get';
import Auth from 'shared/Auth';
import logoEnglish from 'assets/epic-banner2.png';
import logoFrench from 'assets/epic-banner_fr2.png';
const BORDER_COLOR = '#000';
const BORDER_STYLE = 'solid';
const COL1_WIDTH = 50;
const COLN_WIDTH = (100 - COL1_WIDTH) / 2;

const CDRDownload = React.memo(({ displayScore }) => {
  const currentLanguage = get(Auth.getLanguage(), 'lang');
  return currentLanguage === 'en_US' ? (
    <Document>
      <Page style={styles.body}>
        <Image style={styles.logo} cache={false} src={logoEnglish} />

        <Text style={styles.title}> Capacity Diagnostic Report</Text>
        <Text style={styles.text}>
          Thank you for completing the survey! Please find below your capacity diagnostic report.
          This report gives you an overview of your organization’s performance across the eight
          dimensions of organizational capacity assessed by our survey.
        </Text>
        <Text style={styles.text}>
          The report is an excellent benchmark for monitoring and evaluating the evolution of your
          organization’s capacity and effectiveness. It is also a useful tool in conversations with
          your teams and with funders to inspire support for capacity building in priority areas.
        </Text>
        <Text style={styles.text}>
          We congratulate you on your willingness to assess your organization and lead it towards
          even greater impact in your community. EPIC Africa's goal is to strengthen African Civil
          Society Organizations. In addition to providing free tools for self-assessment, we work
          with individual CSOs to address your specific capacity development needs. To learn more
          about our capacity building services, please visit our website or contact
          support@epic-africa.org.
        </Text>
        <Text style={styles.subtitle}>Why focus on organizational capacity?</Text>
        <Text style={styles.text}>
          Achieving social impact takes more than setting clear goals, developing sound theories of
          change, and choosing the right measurement indicators. Turning vision into change on the
          ground also requires a strong capacity to implement programs; ongoing learning and
          adaptation; qualified human resources, and operational structures, processes, and
          capabilities.
        </Text>
        <Text style={styles.text}>
          Because many of the issues that civil society organizations tackle will not be solved in
          the short term, effective, nimble organizations are needed to adaptively address these
          issues over many years. Moreover, CSOs often operate in a context of limited resources and
          are obliged to continually seek creative ways of making tangible progress toward their
          goals and deliver on their missions. This requires sustained organizational capacity
          building. By capacity building we mean
          <Text style={styles.coloredText}>
            “any activity or investment that promotes, sustains, and/or enables the ability of an
            organization to produce desired outcomes.”
          </Text>
        </Text>
        <Text style={styles.text}>
          African CSO leaders and funders recognize the importance of capacity building as a pillar
          of creating sustainable change. Despite this recognition, many barriers prevent both CSOs
          and their funders from embracing a holistic view of organizations. Establishing a direct
          link between building capacity and increased social impact requires perseverance.
          Often-times funders are reluctant to invest in capacity building and CSO leaders are
          preoccupied with keeping overhead costs low and delivering programs and services within
          tight deadlines. Developing and implementing new programs often takes precedence over
          investments in growing, strong organizations.
        </Text>
        <Text style={styles.text}>
          EPIC-Africa sees capacity building not as separate and secondary to program development,
          but as integral to programming and believes that it is a strategic investment that drives
          program quality, implementation and longevity. Our goal is to elevate organizational
          capacity and highlight its importance to achieve program impact. The indicators that we
          have chosen are embedded in a capacity building framework in order to help you identify
          and prioritize actions to make your organizations stronger.
        </Text>
        <Text style={styles.subtitle}>Our Eight Indicators of Organizational Capacity</Text>
        <Text style={styles.text}>
          There are different ways to categorize organizational capacity, but we use the following
          elements as they provide a holistic view of organizations:
        </Text>
        <View style={styles.table}>
          {/* TableHeader */}
          <View style={styles.tableRow}>
            <View style={styles.tableColHeader}>
              <Text style={styles.tableCellHeader}>Capacity Element</Text>
            </View>
            <View
              style={
                get(Auth.getUser(), 'is_legally_registered') ? styles.tableCol1 : styles.tableCol2
              }>
              <Text style={styles.tableCellHeader}>What we are trying to assess?</Text>
            </View>
            {get(Auth.getUser(), 'is_legally_registered') && (
              <View style={styles.tableColHeader}>
                <Text style={styles.tableCellHeader}>How I performed?</Text>
              </View>
            )}
          </View>
          {/* TableContent */}
          <View style={styles.tableRow}>
            <View style={styles.tableCol}>
              <Text style={styles.tableCell}>Strategic Ability and Adaptability</Text>
            </View>
            <View
              style={
                get(Auth.getUser(), 'is_legally_registered') ? styles.tableCol1 : styles.tableCol2
              }>
              <Text style={styles.tableCell}>
                This indicator assesses clarity of strategy, priorities; readiness and ability to
                pursue them; ability to understand and adapt to changing circumstances and
                opportunities. It examines the framework that you have put in place to fulfill your
                mission, e.g. goal setting, measuring success and appropriate resource allocation.
              </Text>
            </View>
            {get(Auth.getUser(), 'is_legally_registered') && (
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>{displayScore('Strategic Ability')}</Text>
              </View>
            )}
          </View>
          <View style={styles.tableRow}>
            <View style={styles.tableCol}>
              <Text style={styles.tableCell}>Leadership and Governance</Text>
            </View>
            <View
              style={
                get(Auth.getUser(), 'is_legally_registered') ? styles.tableCol1 : styles.tableCol2
              }>
              <Text style={styles.tableCell}>
                This indicator evaluates the capacity of your leadership team to guide your
                organization’s work through strategy, vision and inspiration. It also analyzes the
                use of governance structures, which maintain your organization’s accountability,
                reputation and integrity.
              </Text>
            </View>
            {get(Auth.getUser(), 'is_legally_registered') && (
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>{displayScore('Leadership and Governance')}</Text>
              </View>
            )}
          </View>
          <View style={styles.tableRow}>
            <View style={styles.tableCol}>
              <Text style={styles.tableCell}>Financial Health & Organizational Management</Text>
            </View>
            <View
              style={
                get(Auth.getUser(), 'is_legally_registered') ? styles.tableCol1 : styles.tableCol2
              }>
              <Text style={styles.tableCell}>
                This indicator reviews your organization’s resources and income, professionalism of
                financial management and your financial and reporting systems. This review is vital
                to verifying the long-term viability and success of your organization based on sound
                financial management.
              </Text>
            </View>
            {get(Auth.getUser(), 'is_legally_registered') && (
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>{displayScore('Financial Health')}</Text>
              </View>
            )}
          </View>
          <View style={styles.tableRow}>
            <View style={styles.tableCol}>
              <Text style={styles.tableCell}>Human Resources and Staff Development</Text>
            </View>
            <View
              style={
                get(Auth.getUser(), 'is_legally_registered') ? styles.tableCol1 : styles.tableCol2
              }>
              <Text style={styles.tableCell}>
                This indicator analyzes the staff size, quality, and skill sets to address mission
                and drive performance. It also looks at human resource management, which plays a
                strategic role in managing people and the overall culture and direction of your
                organization.
              </Text>
            </View>
            {get(Auth.getUser(), 'is_legally_registered') && (
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>{displayScore('Human Resources')}</Text>
              </View>
            )}
          </View>
          <View style={styles.tableRow}>
            <View style={styles.tableCol}>
              <Text style={styles.tableCell}>Communications</Text>
            </View>
            <View
              style={
                get(Auth.getUser(), 'is_legally_registered') ? styles.tableCol1 : styles.tableCol2
              }>
              <Text style={styles.tableCell}>
                This indicator assesses your organization’s ability to identify, reach and influence
                audiences in line with your mission. A communication strategy ensures that you
                maintain consistent messaging about your work and develop relevant and timely
                material to help you achieve your organizational objectives.
              </Text>
            </View>
            {get(Auth.getUser(), 'is_legally_registered') && (
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>{displayScore('Communications')}</Text>
              </View>
            )}
          </View>
          <View style={styles.tableRow}>
            <View style={styles.tableCol}>
              <Text style={styles.tableCell}>Monitoring and Evaluation</Text>
            </View>
            <View
              style={
                get(Auth.getUser(), 'is_legally_registered') ? styles.tableCol1 : styles.tableCol2
              }>
              <Text style={styles.tableCell}>
                This indicator evaluates whether or not your organization uses performance
                indicators and uses the lessons learned for continuous improvement of your
                organization.
              </Text>
            </View>
            {get(Auth.getUser(), 'is_legally_registered') && (
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>{displayScore('Monitoring')}</Text>
              </View>
            )}
          </View>
          <View style={styles.tableRow}>
            <View style={styles.tableCol}>
              <Text style={styles.tableCell}>Partnerships and Alliances</Text>
            </View>
            <View
              style={
                get(Auth.getUser(), 'is_legally_registered') ? styles.tableCol1 : styles.tableCol2
              }>
              <Text style={styles.tableCell}>
                This indicator measures your organization’s capacity and reputation for creating
                value with others. It evaluates your organization’s use of networks and your
                willingness to collaborate with partners to further organizational goals.
              </Text>
            </View>
            {get(Auth.getUser(), 'is_legally_registered') && (
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>{displayScore('Partnerships')}</Text>
              </View>
            )}
          </View>
          <View style={styles.tableRow}>
            <View style={styles.tableCol}>
              <Text style={styles.tableCell}>Operations</Text>
            </View>
            <View
              style={
                get(Auth.getUser(), 'is_legally_registered') ? styles.tableCol1 : styles.tableCol2
              }>
              <Text style={styles.tableCell}>
                This indicator evaluates whether your organization has adequate tools, resources,
                and structures to effectively manage operational risks and implement programs.
              </Text>
            </View>
            {get(Auth.getUser(), 'is_legally_registered') && (
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>{displayScore('Operations')}</Text>
              </View>
            )}
          </View>
        </View>
        <View>
          <Text style={styles.subtitle}>Our “big 3” Capacity Elements</Text>
          <View>
            <Text style={styles.text}>
              Any organization needs all eight elements to function in a basic, credible manner to
              keep the lights on, operate legally, and make ongoing progress. However, the top three
              categories act as drivers of excellence for many of the other capacities and for the
              overall success of an organization. As a result, the scoring and value placed on the
              “big three” capacity elements are higher than that of the remaining elements. The big
              three elements namely, Strategic Ability and Adaptability; Leadership and Governance
              and Financial Health and Organizational Management are vital capabilities and are
              typically the foundation on which overall organizational capacity is built.
              Organizations should strive to perform very well in these three categories.
              Organizations as well as funders need to invest extra attention, energy, and resources
              to make sure the big three capacity elements are strong and high functioning within an
              organization.
            </Text>
            <Text style={styles.text}>
              The other five elements are no less important than the "big three", rather, each of
              these additional elements of capacity will carry a different weight for each
              organization depending on its typology and its specific goals, and strategy. For
              example, Communications, and Partnerships and Alliances are mission critical for an
              advocacy organization and such organizations should strive to be very good in these
              elements.
            </Text>
          </View>
        </View>
      </Page>
    </Document>
  ) : (
    <Document>
      <Page style={styles.body}>
        <Image style={styles.logo} cache={false} src={logoFrench} />
        <Text style={styles.title}>Rapport d’analyse des capacités organisationnelles</Text>
        <Text style={styles.text}>
          Merci d'avoir répondu au sondage! Veuillez trouver ci-dessous votre rapport de diagnostic
          de capacité.! Veuillez trouver ci-dessous votre rapport de diagnostic des capacités. Ce
          rapport vous donne un aperçu des huit domaines clés de la capacité organisationnelle
          évalués par cette enquête.
        </Text>
        <Text style={styles.text}>
          Ce rapport est un excellent point de référence pour suivre et évaluer l'évolution de la
          capacité et de l'efficacité de votre organisation. C'est également un outil utile dans les
          conversations avec vos équipes et avec les bailleurs de fonds pour inspirer le soutien au
          renforcement des capacités dans les domaines prioritaires.
        </Text>
        <Text style={styles.text}>
          Nous vous félicitons pour votre volonté d'évaluer votre organisation et de la conduire
          vers un impact encore plus important dans votre communauté. L'objectif d'EPIC-Africa est
          de renforcer les organisations de la société civile africaine. En plus de fournir des
          outils gratuits pour l'auto-évaluation, nous travaillons avec des OSC individuelles pour
          répondre à vos besoins spécifiques en matière de renforcement des capacités. Pour en
          savoir plus sur nos services de renforcement des capacités, veuillez visiter notre site
          web www.epic-africa.org ou nous contacter sur support@epic-africa.org.
        </Text>
        <Text style={styles.subtitle}>
          Pourquoi mettre l’accent sur la capacité organisationnelle?
        </Text>
        <Text style={styles.text}>
          Pour avoir un impact social, il ne suffit pas simplement de se fixer des objectifs clairs,
          d’élaborer des théories rationnelles de changement et de choisir les bons indicateurs
          d’évaluation. Pour transformer une vision en un changement concret, il faut également une
          forte capacité de mise en œuvre des programmes, un apprentissage et une adaptation
          continue pour que le travail progresse, ainsi que des structures, des processus et des
          capacités organisationnels et opérationnels.
        </Text>
        <Text style={styles.text}>
          Étant donné que de nombreux problèmes auxquels les organisations de la société civile
          s'attaquent ne seront pas résolus à court terme, des organisations efficaces et agiles
          sont nécessaires pour s'adapter à ces problèmes pendant de nombreuses années. En outre,
          les OSC opèrent souvent dans un contexte de ressources limitées et sont obligées de
          rechercher continuellement des moyens créatifs pour réaliser des progrès concrets vers
          leurs objectifs et remplir leurs missions. Cela nécessite un renforcement durable des
          capacités organisationnelles. Par renforcement des capacités, nous entendons "toute
          activité ou tout investissement qui favorise, soutien et/ou permet à une organisation de
          produire les résultats souhaités".
        </Text>
        <Text style={styles.text}>
          Les dirigeants et les bailleurs de fonds des OSC africaines reconnaissent l'importance du
          renforcement des capacités en tant que pilier de la création d'un changement durable.
          Malgré cette reconnaissance, de nombreux obstacles empêchent les OSC et leurs bailleurs de
          fonds d'adopter une vision holistique des organisations. L'établissement d'un lien direct
          entre le renforcement des capacités et un impact social accru exige de la persévérance.
          Souvent, les bailleurs de fonds sont réticents à investir dans le renforcement des
          capacités ; les dirigeants des OSC aussi sont préoccupés par le maintien de frais de
          fonctionnement à un niveau peu élevé et la mise en œuvre de programmes et de services dans
          des délais serrés. L'élaboration et la mise en œuvre de nouveaux programmes ont souvent la
          priorité sur les investissements dans des organisations fortes, en pleine croissance.
        </Text>
        <Text style={styles.text}>
          EPIC-Africa considère que le renforcement des capacités n'est pas séparé, ni secondaire
          par rapport au développement de programmes mais qu'il fait partie intégrante des
          programmes et estime qu'il s'agit d'un investissement stratégique qui favorise la qualité,
          la mise en œuvre et la longévité des programmes. Notre objectif est de mettre en avant la
          capacité organisationnelle et de souligner son importance pour obtenir un impact sur les
          programmes. Les indicateurs que nous avons choisis s'inscrivent dans un cadre de
          renforcement des capacités afin de vous aider à identifier et à hiérarchiser les actions
          visant à rendre vos organisations plus forte.
        </Text>
        <Text style={styles.subtitle}>Nos huit indicateurs de la capacité organisationnelle</Text>
        <Text style={styles.text}>
          Il existe de nombreuses façons différentes de classer les capacités organisationnelles,
          mais nous utilisons les éléments suivants car ils donnent une vue d'ensemble des
          organisations :
        </Text>
        <View style={styles.table}>
          {/* TableHeader */}
          <View style={styles.tableRow}>
            <View style={styles.tableColHeader}>
              <Text style={styles.tableCellHeader}>Indicateur</Text>
            </View>
            <View
              style={
                get(Auth.getUser(), 'is_legally_registered') ? styles.tableCol1 : styles.tableCol2
              }>
              <Text style={styles.tableCellHeader}>Ce que nous essayons d’évaluer</Text>
            </View>
            {get(Auth.getUser(), 'is_legally_registered') && (
              <View style={styles.tableColHeader}>
                <Text style={styles.tableCellHeader}>Mon résultat</Text>
              </View>
            )}
          </View>
          {/* TableContent */}
          <View style={styles.tableRow}>
            <View style={styles.tableCol}>
              <Text style={styles.tableCell}>Capacité stratégique et adaptabilité</Text>
            </View>
            <View
              style={
                get(Auth.getUser(), 'is_legally_registered') ? styles.tableCol1 : styles.tableCol2
              }>
              <Text style={styles.tableCell}>
                Le présent indicateur évalue la clarté de la stratégie et des priorités, l’état de
                préparation et le pouvoir de les mettre en œuvre, la capacité de comprendre et de
                s’adapter à l’évolution des circonstances et des possibilités.Il examine le cadre
                que vous avez mis en place pour remplir votre mission, c’est-à-dire l’établissement
                d’objectifs, l’évaluation du succès et l’affectation appropriée des ressources.
              </Text>
            </View>
            {get(Auth.getUser(), 'is_legally_registered') && (
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>{displayScore('Strategic Ability')}</Text>
              </View>
            )}
          </View>
          <View style={styles.tableRow}>
            <View style={styles.tableCol}>
              <Text style={styles.tableCell}>Leadership et gouvernance</Text>
            </View>
            <View
              style={
                get(Auth.getUser(), 'is_legally_registered') ? styles.tableCol1 : styles.tableCol2
              }>
              <Text style={styles.tableCell}>
                Cet indicateur évalue la capacité de votre équipe de direction à guider le travail
                de votre organisation à travers sa stratégie, sa vision et son inspiration. Il
                analyse également l’utilisation de structures de gouvernance qui maintiennent
                l’obligation de rendre compte, la réputation et l’intégrité de votre organisation.
              </Text>
            </View>
            {get(Auth.getUser(), 'is_legally_registered') && (
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>{displayScore('Leadership and Governance')}</Text>
              </View>
            )}
          </View>
          <View style={styles.tableRow}>
            <View style={styles.tableCol}>
              <Text style={styles.tableCell}>Santé financière et gestion organisationnelle</Text>
            </View>
            <View
              style={
                get(Auth.getUser(), 'is_legally_registered') ? styles.tableCol1 : styles.tableCol2
              }>
              <Text style={styles.tableCell}>
                Cet indicateur passe en revue les ressources et les revenus de votre organisation,
                le professionnalisme de la gestion financière ainsi que vos systèmes financiers et
                d’établissement de rapports. Cet examen est essentiel à la vérification de la
                viabilité et du succès à long terme de votre organisation, ce qui repose sur une
                bonne gestion financière.
              </Text>
            </View>
            {get(Auth.getUser(), 'is_legally_registered') && (
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>{displayScore('Financial Health')}</Text>
              </View>
            )}
          </View>
          <View style={styles.tableRow}>
            <View style={styles.tableCol}>
              <Text style={styles.tableCell}>
                Développement des ressources humaines et du personnel
              </Text>
            </View>
            <View
              style={
                get(Auth.getUser(), 'is_legally_registered') ? styles.tableCol1 : styles.tableCol2
              }>
              <Text style={styles.tableCell}>
                Cet indicateur analyse l’effectif, la qualité et les compétences du personnel afin
                de répondre à la mission et de stimuler le rendement. Il porte également sur la
                gestion des ressources humaines, qui joue un rôle stratégique dans la gestion des
                personnes ainsi que dans la culture et l'orientation générales de votre
                organisation.
              </Text>
            </View>
            {get(Auth.getUser(), 'is_legally_registered') && (
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>{displayScore('Human Resources')}</Text>
              </View>
            )}
          </View>
          <View style={styles.tableRow}>
            <View style={styles.tableCol}>
              <Text style={styles.tableCell}>Communication</Text>
            </View>
            <View
              style={
                get(Auth.getUser(), 'is_legally_registered') ? styles.tableCol1 : styles.tableCol2
              }>
              <Text style={styles.tableCell}>
                Cet indicateur évalue la capacité de votre organisation à identifier, atteindre et
                influencer le public conformément à votre mission. Une stratégie de communication
                vous permet de maintenir un message cohérent au sujet de votre travail et d’élaborer
                des documents pertinents et opportuns devant vous aider à atteindre vos objectifs
                organisationnels.
              </Text>
            </View>
            {get(Auth.getUser(), 'is_legally_registered') && (
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>{displayScore('Communications')}</Text>
              </View>
            )}
          </View>
          <View style={styles.tableRow}>
            <View style={styles.tableCol}>
              <Text style={styles.tableCell}>Suivi et évaluation</Text>
            </View>
            <View
              style={
                get(Auth.getUser(), 'is_legally_registered') ? styles.tableCol1 : styles.tableCol2
              }>
              <Text style={styles.tableCell}>
                Cet indicateur vise à déterminer si oui ou non votre organisation utilise des
                indicateurs de performance. De plus, il met à profit les enseignements tirés pour
                une amélioration continue de votre organisation.
              </Text>
            </View>
            {get(Auth.getUser(), 'is_legally_registered') && (
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>{displayScore('Monitoring')}</Text>
              </View>
            )}
          </View>
          <View style={styles.tableRow}>
            <View style={styles.tableCol}>
              <Text style={styles.tableCell}>Partenariats et alliances</Text>
            </View>
            <View
              style={
                get(Auth.getUser(), 'is_legally_registered') ? styles.tableCol1 : styles.tableCol2
              }>
              <Text style={styles.tableCell}>
                Il s’agit d’un indicateur qui évalue la réputation de votre organisation et sa
                capacité à créer des alliances avec d’autres structures.. En outre, il vous aide à
                évaluer l’utilisation que fait votre organisation des réseaux, de même que la
                volonté de collaborer avec des partenaires en vue d’avancer sur vos objectifs
                organisationnels.
              </Text>
            </View>
            {get(Auth.getUser(), 'is_legally_registered') && (
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>{displayScore('Partnerships')}</Text>
              </View>
            )}
          </View>
          <View style={styles.tableRow}>
            <View style={styles.tableCol}>
              <Text style={styles.tableCell}>Fonctionnement</Text>
            </View>
            <View
              style={
                get(Auth.getUser(), 'is_legally_registered') ? styles.tableCol1 : styles.tableCol2
              }>
              <Text style={styles.tableCell}>
                Cet indicateur vérifie si votre organisation dispose d’outils, de ressources et de
                structures adéquats pour gérer efficacement les risques opérationnels et mettre en
                œuvre des programmes.
              </Text>
            </View>
            {get(Auth.getUser(), 'is_legally_registered') && (
              <View style={styles.tableCol}>
                <Text style={styles.tableCell}>{displayScore('Operations')}</Text>
              </View>
            )}
          </View>
        </View>
        <View>
          <Text style={styles.subtitle}>
            Nos « 3 principaux » éléments de capacité organisationnelle
          </Text>
          <Text style={styles.text}>
            Toute organisation a besoin de ces huit éléments pour fonctionner de manière
            fondamentale et crédible afin de garder les lumières allumées, de fonctionner légalement
            et de faire des progrès continus. Cependant, les trois premières catégories sont des
            moteurs d'excellence pour de nombreuses autres capacités et pour le succès global d'une
            organisation. Par conséquent, la valeur accordée aux "trois principaux" éléments de la
            capacité organisationnelle sont plus élevées que celles des autres éléments. Les trois
            principaux éléments, à savoir la capacité stratégique et l'adaptabilité, le leadership
            et la gouvernance, la santé financière et la gestion organisationnelle, sont des
            capacités vitales et constituent généralement le fondement sur lequel repose la capacité
            organisationnelle globale. Les organisations doivent s'efforcer d'être "très bonne" dans
            ces catégories. Les organisations ainsi que les bailleurs de fonds doivent investir une
            attention, une énergie et des ressources supplémentaires pour s'assurer que les trois
            principaux éléments de la capacité organisationnelle sont solides et fonctionnent bien
            au sein d'une organisation.
          </Text>
          <Text style={styles.text}>
            Les cinq autres éléments ne sont pas moins importants que ces trois derniers , mais
            chacun de ces éléments de la capacité organisationnelle aura un poids différent pour
            chaque organisation en fonction de sa typologie, de ses objectifs spécifiques et de sa
            stratégie. Par exemple, les indicateurs “communication” et “partenariats et alliances”
            sont essentiels à la mission d'une organisation de défense des droits et ces
            organisations doivent s'efforcer d'être très performantes dans ces domaines.
          </Text>
        </View>
      </Page>
    </Document>
  );
});

export default CDRDownload;

const styles = StyleSheet.create({
  body: {
    paddingTop: 35,
    paddingBottom: 65,
    paddingHorizontal: 35,
  },
  title: {
    fontSize: 18,
    textAlign: 'center',
  },
  author: {
    fontSize: 12,
    textAlign: 'center',
    marginBottom: 40,
  },
  subtitle: {
    fontSize: 18,
    margin: 12,
    textAlign: 'center',
  },
  text: {
    margin: 12,
    fontSize: 14,
    textAlign: 'justify',
    fontFamily: 'Times-Roman',
    color: '#000',
  },
  image: {
    marginVertical: 15,
    marginHorizontal: 100,
  },
  header: {
    fontSize: 14,
    marginBottom: 20,
    textAlign: 'center',
    fontWeight: 'bold',
  },
  pageNumber: {
    position: 'absolute',
    fontSize: 12,
    bottom: 30,
    left: 0,
    right: 0,
    textAlign: 'center',
    color: 'grey',
  },
  table: {
    display: 'flex',
    width: 'auto',
    borderStyle: BORDER_STYLE,
    borderColor: BORDER_COLOR,
    borderWidth: 1,
  },
  tableRow: {
    margin: 'auto',
    flexDirection: 'row',
    borderColor: BORDER_COLOR,
  },
  tableCol1Header: {
    width: COL1_WIDTH + '%',
    borderStyle: BORDER_STYLE,
    borderColor: BORDER_COLOR,
    borderBottomColor: '#000',
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
  },
  tableColHeader: {
    width: COLN_WIDTH + '%',
    borderStyle: BORDER_STYLE,
    borderColor: BORDER_COLOR,
    borderBottomColor: '#000',
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
  },
  tableCol1: {
    width: COL1_WIDTH + '%',
    borderStyle: BORDER_STYLE,
    borderColor: BORDER_COLOR,
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
  },
  tableCol: {
    width: COLN_WIDTH + '%',
    borderStyle: BORDER_STYLE,
    borderColor: BORDER_COLOR,
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
  },
  tableCol2: {
    width: '75%',
    borderStyle: BORDER_STYLE,
    borderColor: BORDER_COLOR,
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
  },
  tableCellHeader: {
    margin: 5,
    fontSize: 14,
    fontWeight: 500,
    borderBottomColor: '#000',
  },
  tableCell: {
    margin: 5,
    fontSize: 12,
  },
  coloredText: {
    margin: 12,
    fontSize: 14,
    textAlign: 'justify',
    fontFamily: 'Times-Roman',
    color: '#EE7D31',
  },
  logo: {
    height: 120,
    width: '90%',
    alignItems: 'center',
    marginHorizontal: 'auto',
    marginBottom: 15,
    marginTop: 10,
  },
});



// WEBPACK FOOTER //
// ./src/components/CDRSections/CDRDownload.js